import React from 'react';
import { 
  FaFlask, 
  FaShieldAlt, 
  FaClock, 
  FaHome, 
  FaUserMd, 
  FaBrain,
  FaCheckCircle,
  FaHeartbeat
} from 'react-icons/fa';

export default function WhyChooseUs() {
  const features = [
    {
      icon: FaFlask,
      title: "Choice of Top Certified Labs",
      description: "Select your preferred diagnostic lab — anytime, anywhere. No arbitrary assignments, no guesswork. You're in charge."
    },
    {
      icon: FaShieldAlt,
      title: "100% NABL & ISO Accredited",
      description: "Every partner lab is verified and NABL-accredited. Your reports are accurate, recognized by all top hospitals and doctors."
    },
    {
      icon: FaClock,
      title: "Express 6-Hour Digital Reports",
      description: "No chasing, no delays. Your verified digital reports are delivered directly to WhatsApp & your patient dashboard."
    },
    {
      icon: FaHome,
      title: "Free Doorstep Sample Pickup",
      description: "No queues, no commute. Certified phlebotomists arrive at your home or office within 60 minutes with safe cold-chain kits."
    },
    {
      icon: FaUserMd,
      title: "DMLT-Certified Phlebotomists",
      description: "Trained and vaccinated professionals arrive fully equipped with barcode-sealed single-use sterile vacuum tubes."
    },
    {
      icon: FaBrain,
      title: "Smart AI Health Insights",
      description: "Get intelligent health trend analysis and free doctor review with every comprehensive full body health checkup."
    }
  ];

  return (
    <div className="py-10 md:py-12 px-4 md:px-8 bg-white rounded-3xl text-slate-800 my-6 shadow-xs border border-slate-200/80">
      <div className="w-full px-2 md:px-4">
        
        {/* ===== HEADER ===== */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-1.5 rounded-full border border-red-100 shadow-2xs mb-3">
            <FaHeartbeat size={14} className="text-red-500" />
            <span className="text-[11px] font-bold tracking-wider uppercase">India's Trusted Diagnostic Network</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">
            Why Book with <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">Medrpha Labs?</span>
          </h2>
          
          <p className="text-slate-500 text-xs md:text-sm mt-2 max-w-2xl mx-auto font-medium">
            Redefining diagnostics with speed, 100% NABL accuracy, and seamless doorstep convenience.
          </p>
        </div>

        {/* ===== STATS RIBBON - LIGHT THEME ===== */}
        <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 bg-slate-50 rounded-2xl p-4 border border-slate-200/80">
          <div className="text-center p-2">
            <p className="text-2xl md:text-3xl font-black text-red-600">10L+</p>
            <p className="text-xs text-slate-600 mt-1 font-semibold">Happy Patients</p>
          </div>
          <div className="text-center p-2 border-l border-slate-200">
            <p className="text-2xl md:text-3xl font-black text-emerald-600">100%</p>
            <p className="text-xs text-slate-600 mt-1 font-semibold">NABL & ISO Verified</p>
          </div>
          <div className="text-center p-2 border-t md:border-t-0 md:border-l border-slate-200">
            <p className="text-2xl md:text-3xl font-black text-amber-600">500+</p>
            <p className="text-xs text-slate-600 mt-1 font-semibold">Partner Labs & Pharmacies</p>
          </div>
          <div className="text-center p-2 border-t md:border-t-0 md:border-l border-slate-200">
            <p className="text-2xl md:text-3xl font-black text-blue-600">4-6 Hrs</p>
            <p className="text-xs text-slate-600 mt-1 font-semibold">Fast WhatsApp Reports</p>
          </div>
        </div>

        {/* ===== FEATURES - LIGHT CARDS ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index} 
                className="group relative bg-slate-50/60 hover:bg-white p-5 rounded-2xl border border-slate-200/80 hover:border-red-300 transition-all duration-300 hover:shadow-md transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-red-100/70 border border-red-200 flex items-center justify-center mb-3.5 group-hover:scale-105 group-hover:bg-red-600 transition-all duration-300">
                  <Icon size={20} className="text-red-600 group-hover:text-white transition-colors" />
                </div>
                
                <h3 className="text-sm md:text-base font-black text-slate-900 mb-1.5 group-hover:text-red-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed font-medium">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* ===== TRUST BADGES ===== */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap justify-center items-center gap-4 md:gap-8 text-xs text-slate-700 font-bold">
          <div className="flex items-center gap-2">
            <FaCheckCircle size={14} className="text-emerald-600" />
            <span>NABL Accredited Diagnostic Center</span>
          </div>
          <div className="flex items-center gap-2">
            <FaCheckCircle size={14} className="text-emerald-600" />
            <span>100% Cold-Chain Temperature Controlled</span>
          </div>
          <div className="flex items-center gap-2">
            <FaCheckCircle size={14} className="text-emerald-600" />
            <span>Certified DMLT Phlebotomists</span>
          </div>
          <div className="flex items-center gap-2">
            <FaCheckCircle size={14} className="text-emerald-600" />
            <span>Encrypted Health Record Vault</span>
          </div>
        </div>
      </div>
    </div>
  );
}